import { notNull } from "@/helpers/utility/Type";
import type { SceneShapeData } from "./sceneShapeData";
import type { SceneNodeItem } from "../scene/SceneNodeItem";
import type { SceneTreeNode } from "../scene/SceneTreeItem";

export function resizeShapePath(
  path: string,
  oldWidth: number,
  oldHeight: number,
  newWidth: number,
  newHeight: number
): string {
  // Calculate the scaling factors
  const widthScale = newWidth / oldWidth;
  const heightScale = newHeight / oldHeight;

  // Update the path by scaling the coordinates
  const scaledPath = path.replace(
    /([MmLlHhVvCcSsQqTtAaZ])([^MmLlHhVvCcSsQqTtAaZ]*)/g,
    (match, command, coords) => {

      const coordsArray = coords
        .trim()
        .split(/[\s,]+/)
        .map(getNumber)
        .filter(notNull);

      const scaledCoords = coordsArray.map((coord: number, index: number) => {
        return index % 2 === 0 ? coord * widthScale : coord * heightScale; // X coordinates are even indices, Y coordinates are odd indices
      });

      return `${command} ${scaledCoords.join(" ")}`;
    }
  );

  return scaledPath;
}

export function createNewShapePath(
  shapeType: SceneShapeData["shapeType"],
  width: number,
  height: number,
  shapeRadius: number = 0
): string {
  const C = 0.552284749831; // Control point constant for circles
  switch (shapeType) {
    case "rectangle":
      return `M0,0 L${width},0 L${width},${height} L0,${height} Z`;
    case "circle":
      const sizeX = width / 2;
      const sizeY = height / 2;
      const centerX = sizeX;
      const centerY = sizeY;
      return `M${centerX},0 C${centerX + sizeX * C},0 ${width},${centerY - sizeY * C} ${width},${centerY} C${width},${centerY + sizeY * C} ${centerX + sizeX * C},${height} ${centerX},${height} C${centerX - sizeX * C},${height} 0,${centerY + sizeY * C} 0,${centerY} C0,${centerY - sizeY * C} ${centerX - sizeX * C},0 ${centerX},0 Z`;
    case "roundedRectangle":
      const radius = Math.min(Math.min(width, height) / 2, shapeRadius);
      const R = radius * 0.45;
      return `M${radius},0 L${width - radius},0 C${width - R},0 ${width},${R} ${width},${radius} L${width},${height - radius} C${width},${height - R} ${width - R},${height} ${width - radius},${height} L${radius},${height} C${R},${height} 0,${height - R} 0,${height - radius} L0,${radius} C0,${R} ${R},0 ${radius},0 Z`;
    case "triangle":
      const halfWidth = width / 2;
      return `M${halfWidth},0 L${width},${height} L0,${height} Z`;
    case "polygon":
      return `M0,0 L${width},0 L${width},${height} L0,${height} Z`; // Default square path
    default:
      return "";
  }
}

function getNumber(value: string): number | null {
  const num = parseFloat(value);
  return isNaN(num) ? null : num;
}

/**
 * Type guard to check if a SceneNodeItem is of dataType "shape"
 */
export function isSceneShapeNode(node: SceneNodeItem): node is SceneNodeItem<"shape"> {
  return node.dataType === "shape";
}

export function isShapeTreeNode(node: SceneTreeNode): node is SceneTreeNode<"shape"> {
  return node.node.dataType === "shape";
}
